"use client";

import { useState, useEffect } from "react";
import { useQuery, useMutation } from "convex/react";
import { api } from "@convex/_generated/api";
import { Id } from "@convex/_generated/dataModel";
import { Button } from "@/components/ui/button";
import { X, Loader2, Camera } from "lucide-react";
import { loadIssuePhoto, deleteIssuePhoto, isNativePlatform } from "@/lib/issue-photo";
import { ImageViewer, IssuePhoto } from "@/components/ui/image-viewer";
import { PhotoSelectionSheet } from "@/components/ui/photo-selection-sheet";
import { saveImageToDevice, shareImage } from "@/lib/image-viewer-utils";

interface IssuePhotoGalleryProps {
  blockId: Id<"blocks">;
  onPhotoAdded?: () => void;
}

export function IssuePhotoGallery({ blockId, onPhotoAdded }: IssuePhotoGalleryProps) {
  const photos = useQuery(api.issuePhotos.getByBlock, { blockId }) ?? [];
  const [loadedImages, setLoadedImages] = useState<Record<string, string>>({});
  const [isDeleting, setIsDeleting] = useState<Id<"issuePhotos"> | null>(null);
  const [viewerOpen, setViewerOpen] = useState(false);
  const [viewerIndex, setViewerIndex] = useState(0);
  const removePhoto = useMutation(api.issuePhotos.remove);

  useEffect(() => {
    if (!isNativePlatform()) return;

    photos.forEach(async (photo) => {
      if (!loadedImages[photo.path]) {
        const dataUrl = await loadIssuePhoto(photo.path);
        if (dataUrl) {
          setLoadedImages((prev) => ({ ...prev, [photo.path]: dataUrl }));
        }
      }
    });
  }, [photos, loadedImages]);

  const handleDelete = async (photoId: Id<"issuePhotos">, path: string) => {
    setIsDeleting(photoId);
    try {
      await deleteIssuePhoto(path);
      await removePhoto({ photoId });
      setLoadedImages((prev) => {
        const next = { ...prev };
        delete next[path];
        return next;
      });
    } catch (error) {
      console.error("Failed to delete photo:", error);
    } finally {
      setIsDeleting(null);
    }
  };

  const handleThumbnailClick = (index: number) => {
    setViewerIndex(index);
    setViewerOpen(true);
  };

  const handleSave = async (imageDataUrl: string) => {
    try {
      await saveImageToDevice(imageDataUrl);
      console.log("Image saved to gallery");
    } catch (error) {
      console.error("Failed to save image:", error);
    }
  };

  const handleShare = async (imageDataUrl: string) => {
    try {
      await shareImage(imageDataUrl, "FlexTraker Issue Photo");
    } catch (error) {
      console.error("Failed to share image:", error);
    }
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <Camera className="h-4 w-4" />
        <span>Route Photos ({photos.length})</span>
      </div>
      <div className="grid grid-cols-3 gap-2">
        {photos.map((photo, index) => (
          <div key={photo._id} className="relative aspect-square rounded-lg overflow-hidden border">
            {loadedImages[photo.path] ? (
              <img
                src={loadedImages[photo.path]}
                alt="Issue"
                className="w-full h-full object-cover cursor-pointer"
                onClick={() => handleThumbnailClick(index)}
              />
            ) : (
              <div className="w-full h-full bg-muted animate-pulse" />
            )}
            {/* Note indicator */}
            {photo.note && (
              <div className="absolute bottom-1 left-1 bg-black/60 text-white text-xs px-1.5 py-0.5 rounded">
                📝
              </div>
            )}
            <Button
              variant="destructive"
              size="icon"
              className="absolute top-1 right-1 h-6 w-6"
              onClick={(e) => {
                e.stopPropagation();
                handleDelete(photo._id, photo.path);
              }}
              disabled={isDeleting === photo._id}
            >
              {isDeleting === photo._id ? (
                <Loader2 className="h-3 w-3 animate-spin" />
              ) : (
                <X className="h-3 w-3" />
              )}
            </Button>
          </div>
        ))}
        {/* Add Photo Button - appears as last slot */}
        <PhotoSelectionSheet
          blockId={blockId}
          trigger={
            <Button
              variant="outline"
              className="aspect-square w-full h-full rounded-lg border-2 border-dashed border-muted-foreground/30 hover:border-primary hover:bg-muted/50 transition-colors"
            >
              <div className="flex flex-col items-center gap-1 text-muted-foreground">
                <Camera className="h-6 w-6" />
                <span className="text-xs">Add Photo</span>
              </div>
            </Button>
          }
          onPhotoAdded={() => {
            onPhotoAdded?.();
          }}
        />
      </div>

      <ImageViewer
        photos={photos as IssuePhoto[]}
        currentIndex={viewerIndex}
        open={viewerOpen}
        onOpenChange={setViewerOpen}
        onIndexChange={setViewerIndex}
        onSave={handleSave}
        onShare={handleShare}
      />
    </div>
  );
}
